import {
	serializeFilterForm,
	checkboxFilterDefaults,
	resetCheckboxFilters,
	applyCheckboxToggles,
	applyHashFilters,
} from '../util/filter-params';

( function ( _s ) {
	/**
	 * Hides/shows the pinned element(s) based on the filters.
	 * If at least one visible filter has a value and the value is either not empty or not the default one
	 * then we hide the pinned element(s), else we show it.
	 *
	 * @param {*} _elements
	 * @param {*} _filters
	 * @returns
	 */
	const toggleVisibilityOnFiltersApplied = ( _elements, _filters ) => {
		//bail early
		if ( ! _elements.length ) return;

		//vars
		const visibleFilters = [];
		const filtersValues = [];
		//a checkbox filter always reports a value ('1'/'0'), so "is it filtered?" means
		//"does it differ from its declared default?" — a checked-by-default toggle must
		//not read as an active filter.
		const checkboxDefaults = checkboxFilterDefaults( _filters );

		//get all the fields that aren't hidden or disabled
		_filters.find( `select, textarea, input[type]` ).each( iEl => {
			if ( ! iEl.disabled && iEl.getAttribute( 'type' ) !== 'hidden' ) {
				//save the name of the inputs that are visible
				visibleFilters.push( iEl.getAttribute( 'name' ) );
			}
		} );

		//get the current filter configuration
		_filters.trigger( 'fr:get-filters-config', {
			callback: data => {
				for ( const [ key, value ] of Object.entries( data ) ) {
					if ( visibleFilters.includes( key ) ) {
						switch ( key ) {
							//NEED TO HANDLE P_TYPE
							case 'order_by':
								filtersValues.push( value.length && value !== 'upcoming_first' );
								break;
							default:
								if ( Object.prototype.hasOwnProperty.call( checkboxDefaults, key ) ) {
									filtersValues.push( value !== checkboxDefaults[ key ] );
									break;
								}
								filtersValues.push( value.length !== 0 );
								break;
						}
					}
				}

				//if at least one filter has value and is not the default value
				_elements.each( iEl => {
					_s( iEl ).toggleClass(
						'd-none',
						filtersValues.some( el => el === true )
					);
				} );
			},
		} );
	};

	/**
	 * Was this change fired by a checkbox filter that only shows/hides already-rendered
	 * markup (`data-fr-no-refetch`)? Those must not re-run the grid query. Anything else
	 * — including Clear filters and the hash deep-link init, which pass no target — does.
	 *
	 * @param {*} target
	 * @returns {boolean}
	 */
	const isNoRefetchTarget = target =>
		!! (
			target &&
			target.hasAttribute &&
			target.hasAttribute( 'data-fr-filter-checkbox' ) &&
			target.hasAttribute( 'data-fr-no-refetch' )
		);

	const updateUrlParams = ( _filters, params ) => {
		const visibleFilters = [];
		//get all the fields that aren't hidden or disabled
		_filters.find( `select, textarea, input[type]` ).each( iEl => {
			if ( ! iEl.disabled && iEl.getAttribute( 'type' ) !== 'hidden' ) {
				//save the name of the inputs that are visible
				visibleFilters.push( iEl.getAttribute( 'name' ) );
			}
		} );

		const cleanedConfig = Object.fromEntries(
			Object.entries( params ).filter( ( [ _, v ] ) => {
				return v.length > 0 && visibleFilters.includes( _ );
			} )
		);

		_s( window ).trigger( 'fr:update-url-params', {
			urlParams: new URLSearchParams( cleanedConfig ).toString(),
		} );
	};

	/**
	 * Update the live result-count toolbar with the total match count.
	 *
	 * @param {*} _countEls
	 * @param {number} count
	 */
	const updateResultsCount = ( _countEls, count ) => {
		if ( ! _countEls.length || count === undefined || count === null ) return;

		const n = parseInt( count, 10 ) || 0;
		const noun = _countEls.attr( 'data-results-noun' ) || 'result';
		const label = `${ n } ${ noun }${ n === 1 ? '' : 's' }`;

		_countEls.each( iEl => {
			iEl.textContent = label;
		} );
	};

	/**
	 * Reset every filter input to its default (radios → the empty "All" option,
	 * search → empty, sort → first option, checkbox filters → their declared
	 * default) and re-run the query via the normal fr:filters-changed path so AJAX +
	 * URL sync stay consistent.
	 *
	 * @param {*} _filters
	 */
	const clearFilters = _filters => {
		_filters.find( 'input[type="search"], input[type="text"]' ).each( iEl => {
			iEl.value = '';
		} );

		//radios: check the empty-valued (All) option of each named group
		const seen = {};
		_filters.find( 'input[type="radio"]' ).each( iEl => {
			const name = iEl.getAttribute( 'name' );
			if ( name === 'p_type' ) return; //leave post-type toggle alone
			if ( iEl.value === '' && ! seen[ name ] ) {
				iEl.checked = true;
				seen[ name ] = true;
			}
		} );

		//sort: back to the first option
		_filters.find( 'select[name="order_by"]' ).each( iEl => {
			iEl.selectedIndex = 0;
		} );

		//checkbox filters: back to their declared default (the design's Clear re-checks
		//the news "Show critical alerts" toggle).
		resetCheckboxFilters( _filters );

		_filters.trigger( 'fr:filters-changed', {
			params: { ...serializeFilterForm( _filters ), ...{ page: 1 } },
		} );
	};

	_s.fn.cardGridWithFilters = function () {
		return this.each( el => {
			const _self = _s( el );
			const _filters = _self.find( '.card-grid-filters' );
			const _toggleVisibilityElements = _self.find( '[fr-hide-on-non-empty-filters]' );
			const _cardContainers = _self.find( '.cards-container' );
			const _countEls = _self.find( '[data-results-count]' );

			//on page load set the filter configuration on the _cardContainers
			_filters.trigger( 'fr:get-filters-config', {
				callback: data => {
					_cardContainers.trigger( 'fr:set-ajax-config', { config: data } );
				},
			} );

			//show/hide whatever a checkbox filter drives (data-fr-toggles) for the state
			//the page loaded in — the server render already honours ?query, this covers
			//a #hash deep link and keeps the two in step from the first paint.
			applyCheckboxToggles( _filters, _self );

			//on filters changed let's do the ajax and toggle visibility of pinned elements
			_filters.on(
				'fr:filters-changed',
				window.debounce( e => {
					//update url params
					updateUrlParams( _filters, e.detail.params );
					//show or hide pinned card
					toggleVisibilityOnFiltersApplied( _toggleVisibilityElements, _filters );
					//show or hide checkbox-filter targets (e.g. the news critical band)
					applyCheckboxToggles( _filters, _self );
					//trigger ajax — unless the control that changed is a checkbox filter
					//declared `refetch => false`, i.e. one that only shows/hides markup
					//that is already on the page. Re-running the query for it would reset
					//the grid to page 1 and throw away everything the user had scrolled in
					//(the news critical band is server-rendered and independent of the
					//grid query, so its toggle needs no refetch).
					if ( isNoRefetchTarget( e.detail.target ) ) return;
					_cardContainers.trigger( 'fr:do-ajax', { config: e.detail.params } );
				}, 100 )
			);

			_cardContainers.on( 'fr:ajax-started', () => {
				//Nothing to do right now here but it might be helpful.
				//For example add a class to the _filters so that all inputs are disabled while the ajax runs
				_filters.addClass( 'disabled-inputs' );
			} );

			_cardContainers.on( 'fr:ajax-ended', () => {
				_filters.removeClass( 'disabled-inputs' );
			} );

			_cardContainers.on( 'fr:cards-added', () => {
				//call the text clamping functionality on cards
				_s( window ).trigger( 'fr:initialize-shave', {
					callback: () => {
						_s( '.card' ).cardInit();
					},
				} );
			} );

			//keep the result-count toolbar live on every AJAX response
			_cardContainers.on( 'fr:results-count', e => {
				updateResultsCount( _countEls, e.detail.count );
			} );

			//Clear filters button
			_filters.find( '[data-filter-clear]' ).on( 'click', () => {
				clearFilters( _filters );
			} );

			//Mobile filter drawer: toggle .filters-open on the form (< lg only; the
			//toggle button is display:none on desktop). Pure UI — no AJAX.
			_filters.find( '[data-filter-toggle]' ).on( 'click', () => {
				const open = _filters.el[ 0 ].classList.toggle( 'filters-open' );
				_filters
					.find( '[data-filter-toggle]' )
					.attr( 'aria-expanded', open ? 'true' : 'false' );
			} );

			//Deep-linking from `#hash` (`#cat=…`, `#sort=…`, `#news-category=…`) — the
			//design reads its params from query OR hash because "the hash survives
			//clean-URL redirects". `?query` is untouched: the server render already
			//applied it, so the hash only fills in filters the query didn't set. Runs
			//LAST so the fr:filters-changed handler above is already listening; the
			//normal event path then does URL sync + pinned card + AJAX for us.
			if ( applyHashFilters( _filters ) ) {
				_filters.trigger( 'fr:filters-changed', {
					params: { ...serializeFilterForm( _filters ), ...{ page: 1 } },
				} );
			}
		} );
	};

	_s( document ).ready( () => {
		_s( '.card-grid-with-filters-module' ).cardGridWithFilters();
	} );
} )( window._s );
